<script setup>
import { ref, computed, nextTick, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { getCatalog, newGroupMember } from './groupCatalogs';

const props = defineProps({
  field: { type: Object, required: true },
  // Mapped attributes (and group tokens) already used anywhere on the form, so the
  // `+ Add` menu hides options that are taken.
  usedMappedAttributes: { type: Array, default: () => [] },
});

const emit = defineEmits(['update', 'delete', 'dragstart']);

const { t } = useI18n();

const showAddMenu = ref(false);
// The add menu is teleported to <body> with fixed positioning so it overlays on
// top and is never clipped by the page card's `overflow-hidden` (e.g. when the
// group is the last field at the bottom of a page).
const addBtn = ref(null);
const menuRef = ref(null);
const MENU_WIDTH = 288; // wide enough for a 2-column option grid
const menuStyle = ref({});

function positionMenu() {
  const el = addBtn.value;
  if (!el) return;
  const r = el.getBoundingClientRect();
  const left = Math.max(8, Math.min(r.left, window.innerWidth - MENU_WIDTH - 8));
  menuStyle.value = {
    position: 'fixed',
    top: `${r.bottom + 4}px`,
    left: `${left}px`,
    width: `${MENU_WIDTH}px`,
  };
}

function onDocMouseDown(e) {
  if (menuRef.value?.contains(e.target) || addBtn.value?.contains(e.target)) return;
  closeMenu();
}

function closeMenu() {
  showAddMenu.value = false;
  document.removeEventListener('mousedown', onDocMouseDown, true);
  window.removeEventListener('scroll', closeMenu, true);
  window.removeEventListener('resize', closeMenu);
}

async function openMenu() {
  showAddMenu.value = true;
  await nextTick();
  positionMenu();
  document.addEventListener('mousedown', onDocMouseDown, true);
  window.addEventListener('scroll', closeMenu, true);
  window.addEventListener('resize', closeMenu);
}

function toggleMenu() {
  if (showAddMenu.value) closeMenu();
  else openMenu();
}

onBeforeUnmount(closeMenu);

const catalog = computed(() => getCatalog(props.field.catalog));

const members = computed(() => props.field.members || []);

const usedSet = computed(() => new Set(props.usedMappedAttributes));

// Catalog options not already added to this group and not used elsewhere on the form.
const availableOptions = computed(() => {
  const taken = new Set(members.value.map(m => m.mapped_attribute));
  return (catalog.value?.options || []).filter(
    opt => !taken.has(opt.mapped_attribute) && !usedSet.value.has(opt.mapped_attribute)
  );
});

function optionLabel(option) {
  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  return t(option.labelKey);
}

function updateLabel(e) {
  emit('update', { ...props.field, label: e.target.value });
}

function addMember(option) {
  const member = newGroupMember(props.field.id, option, t);
  emit('update', { ...props.field, members: [...members.value, member] });
  closeMenu();
}

function removeMember(name) {
  emit('update', {
    ...props.field,
    members: members.value.filter(m => m.name !== name),
  });
}

function handleDragStart(e) {
  emit('dragstart', e);
}
</script>

<template>
  <div
    class="rounded-lg border border-violet-500/30 bg-n-background p-3 flex flex-col gap-3 group transition-colors"
    draggable="true"
    @dragstart="handleDragStart"
  >
    <!-- Header -->
    <div class="flex items-start gap-3">
      <!-- Drag handle -->
      <div
        class="text-n-slate-8 hover:text-n-slate-10 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing"
      >
        <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <circle cx="9" cy="6" r="1.5" />
          <circle cx="15" cy="6" r="1.5" />
          <circle cx="9" cy="12" r="1.5" />
          <circle cx="15" cy="12" r="1.5" />
          <circle cx="9" cy="18" r="1.5" />
          <circle cx="15" cy="18" r="1.5" />
        </svg>
      </div>

      <div class="flex-1 min-w-0">
        <div class="flex items-center gap-2 mb-1">
          <span
            class="text-xs font-medium px-1.5 py-0.5 rounded shrink-0 text-violet-400 bg-violet-400/10"
          >
            {{ t('FORM_BUILDER.GROUP.BADGE') }}
          </span>
          <input
            type="text"
            :value="field.label"
            :placeholder="t('FORM_BUILDER.GROUP.GROUP_NAME')"
            class="text-sm font-medium text-n-slate-12 bg-transparent border-none px-0 flex-1 min-w-0 focus:outline-none focus:ring-0"
            @input="updateLabel"
          />
        </div>
        <p class="text-[10px] text-n-slate-9">
          {{ t('FORM_BUILDER.GROUP.MEMBER_ADD_HINT') }}
        </p>
      </div>

      <!-- Delete -->
      <button
        class="w-8 h-8 rounded flex items-center justify-center text-n-slate-11 hover:text-red-400 hover:bg-n-alpha-2 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
        @click="$emit('delete')"
      >
        <svg
          class="w-[18px] h-[18px]"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          viewBox="0 0 24 24"
        >
          <path d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>

    <!-- Members -->
    <div class="flex flex-col gap-1.5">
      <div
        v-for="member in members"
        :key="member.name"
        class="flex items-center gap-2 rounded-lg bg-n-alpha-1 border border-n-weak px-2.5 py-2"
      >
        <svg
          class="w-3.5 h-3.5 text-violet-400 shrink-0"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          viewBox="0 0 24 24"
        >
          <path :d="member.icon" />
        </svg>
        <span class="text-sm text-n-slate-12 flex-1 min-w-0 truncate">
          {{ member.label }}
        </span>
        <button
          class="text-n-slate-9 hover:text-red-400 transition-colors shrink-0"
          :title="t('FORM_BUILDER.GROUP.REMOVE')"
          @click="removeMember(member.name)"
        >
          <svg
            class="w-4 h-4"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            viewBox="0 0 24 24"
          >
            <path d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <p v-if="!members.length" class="text-[10px] text-n-slate-9 px-1 py-1">
        {{ t('FORM_BUILDER.GROUP.NO_MEMBERS') }}
      </p>
    </div>

    <!-- Add menu -->
    <div>
      <button
        v-if="availableOptions.length"
        ref="addBtn"
        class="inline-flex items-center gap-1.5 text-xs font-medium text-violet-400 hover:text-violet-300 transition-colors"
        @click="toggleMenu"
      >
        <svg
          class="w-3.5 h-3.5"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          viewBox="0 0 24 24"
        >
          <path d="M12 4v16m8-8H4" />
        </svg>
        {{ t('FORM_BUILDER.GROUP.ADD_MEMBER') }}
      </button>

      <!-- Teleported to body + fixed so the page card's overflow-hidden can't clip it. -->
      <Teleport to="body">
        <div
          v-if="showAddMenu && availableOptions.length"
          ref="menuRef"
          :style="menuStyle"
          class="z-[60] rounded-lg border border-n-weak bg-n-background shadow-lg p-1 grid grid-cols-2 gap-0.5"
        >
          <button
            v-for="option in availableOptions"
            :key="option.key"
            class="w-full flex items-center gap-2 px-2.5 py-1.5 rounded text-sm text-n-slate-12 hover:bg-n-alpha-1 text-left"
            @click="addMember(option)"
          >
            <svg
              class="w-3.5 h-3.5 text-violet-400 shrink-0"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              viewBox="0 0 24 24"
            >
              <path :d="option.icon" />
            </svg>
            <span class="truncate">{{ optionLabel(option) }}</span>
          </button>
        </div>
      </Teleport>
    </div>
  </div>
</template>
